import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getPledgeReleaseCaseById,
  updatePledgeReleaseCaseStatus,
  deletePledgeReleaseCase,
  restorePledgeReleaseCase,
  getGoldReceiptByCase,
  generateFinalBill,
  finalizeSettlement,
  listAgreements,
  getReconcileState,
} from '../api';
import { ReconcileButton, ReconcileBanner, ReconcileHistory } from '../components/ReconcileControl';
import { addCaseToBranchVault } from '../../goldVault/api';
import GoldVaultLabel from '../../../components/GoldVaultLabel';
import AdvancePaymentLedgerTable from '../components/AdvancePaymentLedgerTable';
import VoucherTable from '../components/VoucherTable';
import SettlementPaymentVoucherTable from '../components/SettlementPaymentVoucherTable';
import GoldAgreementSection from '../components/GoldAgreementSection';
import DocumentUploadSection from '../components/DocumentUploadSection';
import SettlementPaymentTable from '../components/SettlementPaymentTable';
import CaseProgressStepper, { STEP_KEY_TO_TAB, deriveStepStatuses } from '../components/CaseProgressStepper';
import {
  Card, Tag, Button, Space, Spin, Typography,
  Select, Popconfirm, message, Divider, Row, Col, Table,
  Modal, Statistic, Tabs, Alert,
} from 'antd';
import {
  DollarOutlined, CheckCircleOutlined,
  SafetyOutlined, GoldOutlined,
  ExclamationCircleOutlined, FileTextOutlined,
  LockOutlined, UnlockOutlined, InfoCircleOutlined, AuditOutlined,
  FileDoneOutlined, SafetyCertificateOutlined,
  UserOutlined, BankOutlined, ProfileOutlined,
} from '@ant-design/icons';
import { SummarySection, SummaryLayout, InfoGrid } from '../../../components/CaseSummary';
import { calculateCaseFinalAmount, calculateCaseRoundOff, formatRoundOff } from '../../../utils/goldCalculation';
import dayjs from 'dayjs';
import { useDeleteConfirm } from '../../../components/DeleteConfirm';
import { usePermissions } from '../../../hooks/usePermissions';
import 'bootstrap/dist/css/bootstrap.min.css';

const { Title, Text } = Typography;

const STATUS_OPTIONS = [
  'LEAD_IDENTIFIED',
  'DOCS_VERIFIED',
  'ADVANCE_APPROVED',
  'PART_PAYMENT_DONE',
  'FULLY_PAID_AWAITING_RELEASE',
  'GOLD_RECEIVED',
  'VALUATION_COMPLETED',
  'PAYMENT_SETTLED',
  'BILL_GENERATED',
  'CLOSED',
];

const STATUS_COLORS = {
  LEAD_IDENTIFIED: 'blue',
  DOCS_VERIFIED: 'cyan',
  ADVANCE_APPROVED: 'geekblue',
  PART_PAYMENT_DONE: 'orange',
  FULLY_PAID_AWAITING_RELEASE: 'gold',
  GOLD_RECEIVED: 'lime',
  VALUATION_COMPLETED: 'purple',
  PAYMENT_SETTLED: 'volcano',
  BILL_GENERATED: 'magenta',
  CLOSED: 'green',
};

const formatCurrency = (val) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(val || 0);

// Item prices, gold value and processing fee are exact — shown to 4 decimals.
const formatCurrency4 = (val) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(val || 0);

const PledgeReleaseCaseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEditModule, can, canViewField } = usePermissions();

  const canViewCustomer = canViewField('pledgeReleaseCase', 'customerId');
  const canViewLender = canViewField('pledgeReleaseCase', 'lenderName');
  const canViewLoanAccount = canViewField('pledgeReleaseCase', 'loanAccountNumber');
  const canViewClosure = canViewField('pledgeReleaseCase', 'closureAmount');
  const canViewGoldItems = canViewField('pledgeReleaseCase', 'goldItems');
  const canViewStatusField = canViewField('pledgeReleaseCase', 'status');
  const canViewCapitalExposure = canViewField('pledgeReleaseCase', 'capitalExposure');
  const canViewActualProfit = canViewField('pledgeReleaseCase', 'actualProfit');
  const canViewFinalGoldValue = canViewField('pledgeReleaseCase', 'finalGoldValue');

  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // Active tab — will be set to the progress-based tab once caseData loads
  const [activeTab, setActiveTab] = useState('summary');
  const [tabsInitialized, setTabsInitialized] = useState(false);

  // Gold Receipt state
  const [goldReceipt, setGoldReceipt] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [settling, setSettling] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  // Agreements state (for stepper)
  const [agreements, setAgreements] = useState([]);

  // Reconciliation — the approval-gated way back into a locked case
  const [reconcileState, setReconcileState] = useState(null);

  // Modal hook (antd v5)
  const [modal, modalContextHolder] = Modal.useModal();

  // Vault state
  const [vaultLoading, setVaultLoading] = useState(false);
  const [vaultLabelVisible, setVaultLabelVisible] = useState(false);

  // Voucher refresh trigger
  const [voucherRefreshTrigger, setVoucherRefreshTrigger] = useState(0);

  // Section refs for scroll
  const sectionRefs = useRef({});
  const registerRef = useCallback((key) => (el) => { sectionRefs.current[key] = el; }, []);

  /* ─── Data loaders ─── */
  const loadCase = async () => {
    setLoading(true);
    try {
      const res = await getPledgeReleaseCaseById(id);
      setCaseData(res.data);
    } catch (err) {
      console.error('Error loading case:', err);
      messageApi.error('Failed to load case');
    } finally {
      setLoading(false);
    }
  };

  const loadGoldReceipt = async () => {
    setReceiptLoading(true);
    try {
      const res = await getGoldReceiptByCase(id);
      setGoldReceipt(res.data || null);
    } catch (err) {
      if (err?.response?.status !== 404) {
        console.error('Error loading gold receipt:', err);
      }
      setGoldReceipt(null);
    } finally {
      setReceiptLoading(false);
    }
  };

  const loadAgreements = async () => {
    try {
      const res = await listAgreements(id, { limit: 100 });
      setAgreements(res.data || res.agreements || []);
    } catch {
      setAgreements([]);
    }
  };

  const loadReconcileState = async () => {
    try {
      const res = await getReconcileState(id);
      setReconcileState(res.data || null);
    } catch {
      setReconcileState(null);
    }
  };

  useEffect(() => {
    loadCase();
    loadGoldReceipt();
    loadAgreements();
    loadReconcileState();
  }, [id]);

  /* ─── Auto-select the correct tab when case data loads ─── */
  useEffect(() => {
    if (caseData && !tabsInitialized) {
      // Find the active step from the progress stepper logic
      const steps = deriveStepStatuses(caseData, goldReceipt, agreements);
      const activeStep = steps.find(s => s.status === 'active');
      let targetTab = 'summary';
      if (activeStep && STEP_KEY_TO_TAB[activeStep.key]) {
        targetTab = STEP_KEY_TO_TAB[activeStep.key];
      }
      setActiveTab(targetTab);
      setTabsInitialized(true);
    }
  }, [caseData, goldReceipt, agreements, tabsInitialized]);

  /* ─── Tab order for Next button ─── */
  // Settlement now precedes Bill: the customer is paid first, and the bill is
  // raised last so it can carry the full split-payment trail.
  const TAB_ORDER = [
    'summary',
    'documents',
    'pre-loan-agreement',
    'advances',
    'gold-receipt',
    'final-agreement',
    'settlement',
    'bill',
  ];

  /* ─── Completed tabs (all tabs before the current progress tab) ─── */
  const getTabState = (tabKey) => {
    if (!caseData) return 'pending';
    const steps = deriveStepStatuses(caseData, goldReceipt, agreements);
    const activeStep = steps.find(s => s.status === 'active');
    let currentTabFromStatus = 'summary';
    if (activeStep && STEP_KEY_TO_TAB[activeStep.key]) {
      currentTabFromStatus = STEP_KEY_TO_TAB[activeStep.key];
    }
    const currentIdx = TAB_ORDER.indexOf(currentTabFromStatus);
    const tabIdx = TAB_ORDER.indexOf(tabKey);
    if (tabIdx < currentIdx) return 'done';
    if (tabIdx === currentIdx) return 'current';
    return 'pending';
  };

  const goToNextTab = async () => {
    // If we are leaving the documents tab and documents are approved, auto-advance the status
    if (activeTab === 'documents' && caseData?.documentVerification?.status === 'APPROVED' && caseData?.status === 'LEAD_IDENTIFIED') {
      await handleStatusChange('DOCS_VERIFIED');
    }

    const currentIdx = TAB_ORDER.indexOf(activeTab);
    if (currentIdx < TAB_ORDER.length - 1) {
      setActiveTab(TAB_ORDER[currentIdx + 1]);
    }
  };

  const nextTabButton = (() => {
    if (!caseData) return null;

    const steps = deriveStepStatuses(caseData, goldReceipt, agreements);
    const activeStep = steps.find(s => s.status === 'active');
    let progressTab = 'summary';
    if (activeStep && STEP_KEY_TO_TAB[activeStep.key]) {
      progressTab = STEP_KEY_TO_TAB[activeStep.key];
    }

    const currentIdx = TAB_ORDER.indexOf(activeTab);
    const progressIdx = TAB_ORDER.indexOf(progressTab);

    // If it's the last tab, no next button
    if (currentIdx >= TAB_ORDER.length - 1) return null;

    // "next tab will not come for the privious tabs"
    // Hide for any tab older than the one just completed.
    if (currentIdx < progressIdx - 1) {
      return null;
    }

    // Hide for future locked tabs as well
    if (currentIdx > progressIdx) {
      return null;
    }

    // "untill all the requirment for that tab is not field it will be dissabled"
    // If currentIdx == progressIdx, requirements are not met -> Disabled
    // If currentIdx == progressIdx - 1, requirements are met (tab just completed) -> Enabled
    let isDisabled = currentIdx === progressIdx;

    // Always enable Next on Summary tab as requested
    if (activeTab === 'summary') {
      isDisabled = false;
    }

    // Enable Next on Documents tab if documents are APPROVED
    if (activeTab === 'documents' && caseData?.documentVerification?.status === 'APPROVED') {
      isDisabled = false;
    }

    // Enable Next on Pre-Loan Agreement tab if the agreement is generated
    const hasPreLoan = (agreements || []).some(a => a.agreementType === 'PRE_LOAN_TAKEOVER');
    if (activeTab === 'pre-loan-agreement' && hasPreLoan) {
      isDisabled = false;
    }

    // Enable Next on Final Agreement tab if the agreement is generated
    const hasFinal = (agreements || []).some(a => a.agreementType === 'FINAL_PURCHASE');
    if (activeTab === 'final-agreement' && hasFinal) {
      isDisabled = false;
    }

    // Enable Next on Settlement once the customer has been paid in full — that
    // is what unlocks the Bill, which is now the step after.
    const settlementCleared = caseData?.status === 'PAYMENT_SETTLED'
      || caseData?.status === 'CLOSED'
      || (!!caseData?.settlementFinalizedAt
        && ((caseData?.finalSettlementAmount || 0) - (caseData?.totalSettlementPaid || 0)) <= 0.5);
    if (activeTab === 'settlement' && settlementCleared) {
      isDisabled = false;
    }

    return (
      <button
        onClick={goToNextTab}
        disabled={isDisabled}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 16px',
          background: isDisabled ? '#f5f5f5' : '#177f44ff',
          color: isDisabled ? '#b8b8b8' : '#fff',
          border: isDisabled ? '1px solid #d9d9d9' : 'none',
          borderRadius: 6,
          fontSize: 13,
          fontWeight: 600,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          boxShadow: isDisabled ? 'none' : '0 2px 0 rgba(5, 145, 255, 0.1)',
          marginRight: 8,
          transition: 'all 0.2s',
        }}
      >
        NEXT STEP &nbsp;&#8594;
      </button>
    );
  })();

  /* ─── Callbacks ─── */
  const handlePaymentAdded = () => {
    loadCase();
    setVoucherRefreshTrigger((prev) => prev + 1);
  };

  const handleAgreementCreated = () => {
    loadCase();
    loadAgreements();
  };

  const handleDocumentsChanged = () => {
    loadCase();
  };

  /* ─── Derived ─── */
  // The bill is the LAST step — it only unlocks once the customer is fully paid.
  const canGenerateBill = caseData?.status === 'PAYMENT_SETTLED' && !caseData?.billId && !!goldReceipt;
  const isCaseLocked = ['PAYMENT_SETTLED', 'BILL_GENERATED', 'CLOSED'].includes(caseData?.status);

  /* ─── Reconciliation ─── */
  // An approved reconciliation is a single-use licence to correct ONE target.
  // While it is live, that target — and only that target — becomes editable
  // again; saving spends it and the case re-locks itself.
  const reconciliation = reconcileState?.reconciliation;
  const unlockedTarget = reconciliation?.status === 'UNLOCKED' ? reconciliation.target : null;
  const caseDetailsUnlocked = unlockedTarget === 'CASE_DETAILS';
  const goldReceiptUnlocked = unlockedTarget === 'GOLD_RECEIPT';
  const canReconcile = can('pledgeReleaseCase', 'reconcile');

  const refreshAfterReconcile = () => {
    loadCase();
    loadGoldReceipt();
    loadReconcileState();
  };

  const handleEditReconcileTarget = (target) => {
    if (target === 'GOLD_RECEIPT') {
      navigate(`/pledge-release-cases/gold-receipt/edit?caseId=${caseData?._id}`);
    } else {
      navigate(`/pledge-release-cases/edit/${caseData?._id}`);
    }
  };
  // Settlement payouts need the figures locked in first (finalizeSettlement).
  const isSettlementReady = !!caseData?.settlementFinalizedAt || !!caseData?.billId;

  /* ─── Settlement preview ─── */
  const previewSettlement = () => {
    if (!caseData || !goldReceipt) return null;
    const finalNetValue = goldReceipt.finalNetValue || 0;
    const finalGoldValue = goldReceipt.finalGoldValue || 0;
    // Read the fee and the rounding straight off the receipt. Deriving the fee
    // as (gold value − net value) silently folds the rounding-up into it and
    // goes negative whenever the rounding exceeds the fee.
    const processingFeeAmount = goldReceipt.processingFeeAmount || 0;
    const processingFeePercent = goldReceipt.processingFeePercent || 0;
    const roundingUpAmount = goldReceipt.roundingUpAmount || 0;
    const totalLoanPaid = caseData.totalLoanPaid || 0;
    const totalCustomerAdvancePaid = caseData.totalCustomerAdvancePaid || 0;
    const capitalExposure = caseData.capitalExposure || 0;
    const netSettlement = parseFloat((finalNetValue - totalLoanPaid - totalCustomerAdvancePaid).toFixed(2));
    const profit = parseFloat((finalNetValue - capitalExposure).toFixed(2));
    const profitPct = capitalExposure > 0 ? parseFloat(((profit / capitalExposure) * 100).toFixed(2)) : 0;
    return {
      finalGoldValue, finalNetValue, processingFeeAmount, processingFeePercent, roundingUpAmount,
      totalLoanPaid, totalCustomerAdvancePaid, capitalExposure, netSettlement, profit, profitPct,
    };
  };

  /**
   * Lock in what the customer is owed so the payouts can start. Runs before any
   * payment — the bill comes after them. Safe to call repeatedly: the backend
   * only recomputes while nothing has been paid yet.
   */
  const ensureSettlementFinalized = async () => {
    if (!caseData || isSettlementReady) return;
    if (caseData.status !== 'VALUATION_COMPLETED' || !goldReceipt) return;

    setFinalizing(true);
    try {
      await finalizeSettlement(id);
      await loadCase();
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to prepare settlement');
    } finally {
      setFinalizing(false);
    }
  };

  const handleGenerateBill = () => {
    const preview = previewSettlement();
    if (!preview) {
      messageApi.warning('Unable to compute settlement summary — ensure Gold Receipt is loaded.');
      return;
    }

    modal.confirm({
      title: 'Generate Final Bill & Close Case',
      icon: <ExclamationCircleOutlined />,
      width: 560,
      content: (
        <div style={{ marginTop: 12 }}>
          <p style={{ marginBottom: 16 }}>
            The customer has been paid in full. This creates the official <strong>Bill</strong> from the Gold
            Receipt, carrying every settlement payment and its reference number, and closes the case
            as <Tag color="green">CLOSED</Tag>. <strong>This action cannot be undone.</strong>
          </p>
          <Card size="small" style={{ background: '#fafafa', marginBottom: 8 }}>
            <Row gutter={[12, 8]}>
              <Col span={14}><Text type="secondary">Final Gold Value</Text></Col>
              <Col span={10} style={{ textAlign: 'right' }}><Text strong>{formatCurrency4(preview.finalGoldValue)}</Text></Col>

              {preview.processingFeeAmount > 0 && (
                <>
                  <Col span={14}>
                    <Text type="secondary">
                      Processing Fee Deducted{preview.processingFeePercent > 0 ? ` (${preview.processingFeePercent}%)` : ''}
                    </Text>
                  </Col>
                  <Col span={10} style={{ textAlign: 'right' }}>
                    <Text type="danger">- {formatCurrency4(preview.processingFeeAmount)}</Text>
                  </Col>
                </>
              )}

              {preview.roundingUpAmount !== 0 && (
                <>
                  <Col span={14}><Text type="secondary">Rounding Up</Text></Col>
                  <Col span={10} style={{ textAlign: 'right' }}>
                    <Text style={{ color: preview.roundingUpAmount > 0 ? '#52c41a' : '#cf1322' }}>
                      {preview.roundingUpAmount > 0 ? '+ ' : '- '}{formatCurrency(Math.abs(preview.roundingUpAmount))}
                    </Text>
                  </Col>
                </>
              )}

              {calculateCaseRoundOff(preview.finalGoldValue, preview.processingFeeAmount, preview.roundingUpAmount, preview.finalNetValue) !== 0 && (
                <>
                  <Col span={14}><Text type="secondary">Round Off (to nearest ₹)</Text></Col>
                  <Col span={10} style={{ textAlign: 'right' }}>
                    <Text type="secondary">
                      {formatRoundOff(calculateCaseRoundOff(preview.finalGoldValue, preview.processingFeeAmount, preview.roundingUpAmount, preview.finalNetValue))}
                    </Text>
                  </Col>
                </>
              )}

              <Col span={14}><Text type="secondary">Final Net Value</Text></Col>
              <Col span={10} style={{ textAlign: 'right' }}><Text strong style={{ color: '#3f8600' }}>{formatCurrency(preview.finalNetValue)}</Text></Col>

              <Col span={24}><Divider style={{ margin: '8px 0' }} /></Col>

              <Col span={14}><Text type="secondary">Loan Paid (to Lender)</Text></Col>
              <Col span={10} style={{ textAlign: 'right' }}><Text>- {formatCurrency(preview.totalLoanPaid)}</Text></Col>

              <Col span={14}><Text type="secondary">Customer Advance Paid</Text></Col>
              <Col span={10} style={{ textAlign: 'right' }}><Text>- {formatCurrency(preview.totalCustomerAdvancePaid)}</Text></Col>

              <Col span={24}><Divider style={{ margin: '8px 0' }} /></Col>

              <Col span={14}><Text strong style={{ fontSize: 15 }}>Net Settlement Amount</Text></Col>
              <Col span={10} style={{ textAlign: 'right' }}>
                <Text strong style={{ fontSize: 15, color: preview.netSettlement >= 0 ? '#3f8600' : '#cf1322' }}>
                  {formatCurrency(preview.netSettlement)}
                </Text>
              </Col>

              <Col span={14}><Text strong>Already Paid to Customer</Text></Col>
              <Col span={10} style={{ textAlign: 'right' }}>
                <Text strong style={{ color: '#3f8600' }}>
                  {formatCurrency(caseData?.totalSettlementPaid)}
                </Text>
              </Col>

            </Row>
          </Card>
        </div>
      ),
      okText: 'Generate Bill & Close',
      okType: 'primary',
      okButtonProps: { icon: <FileTextOutlined /> },
      cancelText: 'Cancel',
      onOk: async () => {
        setSettling(true);
        try {
          const result = await generateFinalBill(id);
          const billCode = result.data?.settlement?.billId || result.data?.bill?.billId || '';
          messageApi.success(`Bill ${billCode} generated — case closed successfully`);
          await Promise.all([loadCase(), loadGoldReceipt(), loadAgreements()]);
        } catch (err) {
          console.error('Generate Final Bill error:', err);
          messageApi.error(err?.response?.data?.message || 'Failed to generate final bill');
        } finally {
          setSettling(false);
        }
      },
    });
  };

  const handleStatusChange = async (newStatus) => {
    setStatusUpdating(true);
    try {
      await updatePledgeReleaseCaseStatus(id, newStatus);
      messageApi.success('Status updated');
      loadCase();
    } catch (err) {
      console.error('Status update error:', err);
      messageApi.error('Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const { confirmDelete, confirmPermanentDelete, confirmRestore, contextHolder: deleteModalHolder, isAdmin } = useDeleteConfirm();

  const handleDelete = async (permanent) => {
    try {
      await deletePledgeReleaseCase(id, { permanent });
      messageApi.success(permanent ? 'Case permanently deleted' : 'Case deleted');
      setTimeout(() => navigate('/pledge-release-cases'), 500);
    } catch (err) {
      console.error('Delete error:', err);
      messageApi.error(err?.response?.data?.message || 'Failed to delete case');
    }
  };

  const handleRestore = async () => {
    try {
      await restorePledgeReleaseCase(id);
      messageApi.success('Case restored');
      await loadCase();
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to restore case');
    }
  };

  const handleMoveToVault = async () => {
    const branchId = caseData?.branch?._id || caseData?.branch;
    if (!branchId) {
      messageApi.error('Branch not found on case — cannot move to vault');
      return;
    }
    setVaultLoading(true);
    try {
      await addCaseToBranchVault(branchId, { caseId: caseData._id, caseModel: 'PledgeReleaseCase' });
      messageApi.success('Gold moved to branch vault');
      await loadCase();
      setVaultLabelVisible(true);
    } catch (e) {
      messageApi.error(e?.response?.data?.message || 'Failed to move to vault');
    } finally {
      setVaultLoading(false);
    }
  };

  /* ─── Opening the Settlement tab locks in the amount to be paid ─── */
  // Deterministic from the gold receipt, so this is a computation the user would
  // otherwise have to trigger by hand before every case's first payout.
  useEffect(() => {
    if (activeTab === 'settlement') ensureSettlementFinalized();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, caseData?.status, caseData?.settlementFinalizedAt, goldReceipt]);

  /* ─── Stepper click → switch tab ─── */
  const handleStepClick = (stepKey) => {
    const tabKey = STEP_KEY_TO_TAB[stepKey];
    if (tabKey) setActiveTab(tabKey);
  };

  /* ─── Loading / empty states ─── */
  // Only the very first load swaps the page for a spinner. Later refetches
  // (after an upload, a payment, a status change) must keep the tab tree
  // mounted — unmounting it would throw away in-progress form input in
  // DocumentUploadSection and friends.
  if (loading && !caseData) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Text type="secondary">Case not found.</Text>
      </div>
    );
  }

  const c = caseData;

  // Settlement / vault gate
  const pledgeSettlementAmt = c.finalSettlementAmount || 0;
  const pledgeTotalPaid = c.totalSettlementPaid || 0;
  // Derived, not read from remainingSettlementBalance: that field is a cache and
  // cases priced before payouts moved ahead of billing still carry its default of 0.
  const pledgeRemaining = parseFloat((pledgeSettlementAmt - pledgeTotalPaid).toFixed(2));
  const isFullyPaid = isSettlementReady && pledgeRemaining <= 0.5;
  const notYetVaulted = !['IN_BRANCH_VAULT', 'IN_TRANSFER', 'IN_HO_VAULT', 'BATCHED'].includes(c.inventoryStatus);
  // The gold is only shelved once the Bill exists — billing is the last step of
  // the case, and the vault entry has to sit behind a completed purchase.
  const isBilled = !!c.billId || ['BILL_GENERATED', 'CLOSED'].includes(c.status);
  const canMoveToVault = notYetVaulted && isFullyPaid && isBilled;

  const estimatedTotal = c.estimatedTotalAmount || 0;
  const procFeeAmt = c.processingFeeAmount || 0;
  const roundingUpAmt = c.roundingUpAmount || 0;
  const approxFinal = calculateCaseFinalAmount(estimatedTotal, procFeeAmt, roundingUpAmt);
  const estRoundOff = calculateCaseRoundOff(estimatedTotal, procFeeAmt, roundingUpAmt, approxFinal);
  const takeHomeEligible = approxFinal - (c.closureAmount || 0);
  const remainingTakeHome = takeHomeEligible - (c.totalCustomerAdvancePaid || 0);

  /* ════════════════════════════════════════
     TAB CONTENT DEFINITIONS
     ════════════════════════════════════════ */

  /* ── Styled tab labels based on progress ── */
  const styledTabLabel = (tabKey, icon, text) => {
    const state = getTabState(tabKey);

    if (state === 'done') {
      return (
        <span style={{ color: '#475569', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <CheckCircleOutlined />
          {text}
        </span>
      );
    } else if (state === 'current') {
      return (
        <span style={{ color: '#1677ff', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {icon}
          {text}
        </span>
      );
    } else {
      return (
        <span style={{ color: '#8c8c8c', fontWeight: 400, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {icon}
          {text}
        </span>
      );
    }
  };

  const renderNextStepInfo = (text) => (
    <Alert
      message={<span style={{ fontWeight: 500 }}>{text}</span>}
      type="info"
      showIcon
      style={{ marginBottom: 16 }}
    />
  );

  const tabItems = [
    /* ── 1. Summary ── */
    {
      key: 'summary',
      label: styledTabLabel('summary', <InfoCircleOutlined />, ' Summary'),
      children: (
        <div ref={registerRef('summary')}>
          {renderNextStepInfo("Review the case summary to ensure accuracy, then click NEXT STEP to proceed.")}
          <SummaryLayout>
            {canViewCustomer && (
              <SummarySection title="Customer" icon={<UserOutlined />}>
                <InfoGrid
                  fields={[
                    { label: 'Name', value: c.customerId?.name },
                    { label: 'Phone', value: c.customerId?.phone, mono: true },
                    { label: 'Aadhaar', value: c.customerId?.aadhaar, mono: true },
                    { label: 'Email', value: c.customerId?.email },
                    { label: 'Address', value: c.customerId?.address, wide: true },
                  ]}
                />
              </SummarySection>
            )}

            {(canViewLender || canViewLoanAccount || canViewClosure) && (
              <SummarySection
                title="Pledged Loan"
                icon={<AuditOutlined />}
                extra={c.isRateLocked
                  ? <Tag color="green" icon={<LockOutlined />} style={{ margin: 0 }}>Rate Locked</Tag>
                  : <Tag icon={<UnlockOutlined />} style={{ margin: 0 }}>Rate Not Locked</Tag>}
              >
                <InfoGrid
                  fields={[
                    canViewLender && { label: 'Lender', value: c.lenderName },
                    canViewLoanAccount && { label: 'Loan A/c No.', value: c.loanAccountNumber, mono: true },
                    canViewClosure && { label: 'Principal Amount', value: formatCurrency(c.principalAmount) },
                    canViewClosure && { label: 'Interest Amount', value: formatCurrency(c.interestAmount) },
                    canViewClosure && { label: 'Penal Charges', value: formatCurrency(c.penalCharges) },
                    canViewClosure && { label: 'Closure Valid Till', value: c.closureValidTill && dayjs(c.closureValidTill).format('DD MMM YYYY') },
                    canViewClosure && { label: 'Closure Amount', value: <strong>{formatCurrency(c.closureAmount)}</strong> },
                    canViewClosure && { label: 'Total Advance Paid', value: formatCurrency(c.totalAdvancePaid) },
                    canViewClosure && { label: 'Total Loan Paid', value: formatCurrency(c.totalLoanPaid) },
                    canViewClosure && { label: 'Customer Advance Paid', value: formatCurrency(c.totalCustomerAdvancePaid) },
                    canViewClosure && {
                      label: 'Remaining Take-Home',
                      value: <Text strong type={remainingTakeHome < 0 ? 'danger' : 'success'}>{formatCurrency(remainingTakeHome)}</Text>,
                    },
                  ]}
                />
              </SummarySection>
            )}

            {/* Where a bank payout actually lands — the same details the approver
                is shown when they release the settlement or an advance. */}
            {canViewCustomer && (
              <SummarySection title="Bank Payout Details" icon={<BankOutlined />}>
                <InfoGrid
                  fields={[
                    { label: 'Bank A/c No.', value: c.bankAccountNumber, mono: true },
                    { label: 'IFSC', value: c.ifscCode, mono: true },
                    { label: 'Bank Branch', value: c.bankBranchName, wide: true },
                  ]}
                />
              </SummarySection>
            )}

            <SummarySection title="Case Information" icon={<InfoCircleOutlined />}>
              <InfoGrid
                fields={[
                  { label: 'Created By', value: c.createdBy?.name },
                  { label: 'Created At', value: c.createdAt && dayjs(c.createdAt).format('DD MMM YYYY, hh:mm A') },
                  { label: 'Last Updated', value: c.updatedAt && dayjs(c.updatedAt).format('DD MMM YYYY, hh:mm A') },
                  canViewCapitalExposure && { label: 'Capital Exposure', value: formatCurrency(c.capitalExposure) },
                  c.agentId && { label: 'Agent', value: `${c.agentId.name || '-'}${c.agentId.agentId ? ` (${c.agentId.agentId})` : ''}` },
                  c.agentId && {
                    label: 'Agent Commission',
                    value: `${c.agentCommissionPercent ?? 0}% · ${formatCurrency(c.agentCommissionAmount)}`,
                  },
                ]}
              />
            </SummarySection>

            {canViewCustomer && (
              <SummarySection title="Additional Details" icon={<ProfileOutlined />} full>
                <InfoGrid
                  columns={3}
                  fields={[
                    { label: 'Reason for Selling Gold', value: c.reasonForSelling },
                    { label: 'Gold Ownership', value: c.verificationDetails?.goldOwnership },
                    { label: 'Alternate Phone No.', value: c.alternatePhone, mono: true },
                    { label: 'Relative / Reference Person', value: c.referenceDetails },
                    { label: 'Reference Phone No.', value: c.referencePhone, mono: true },
                    {
                      label: 'Current Address',
                      value: c.isAddressSameAsAadhaar === false ? c.currentAddress : 'Same as Aadhaar',
                    },
                  ]}
                />
              </SummarySection>
            )}
          </SummaryLayout>

          {/* Gold Items Table */}
          {canViewGoldItems && c.goldItems && c.goldItems.length > 0 && (
            <>
              <Divider orientation="left">Estimated Gold Items</Divider>
              <Table
                dataSource={c.goldItems.map((item, idx) => ({ ...item, key: idx }))}
                pagination={false}
                size="small"
                bordered
                scroll={{ x: 'max-content' }}
                columns={[
                  { title: '#', render: (_, __, idx) => idx + 1, width: 50 },
                  { title: 'Item Name', dataIndex: 'itemName' },
                  { title: 'Purity (%)', dataIndex: 'purity', render: (v) => `${v}%` },
                  { title: 'Gross Wt. (gm)', dataIndex: 'grossWeight', render: (v) => v?.toFixed(4) },
                  { title: 'Stone/Wax Ded. (gm)', dataIndex: 'stoneWaxDeduction', render: (v) => v?.toFixed(4) },
                  { title: 'Dust Ded. (gm)', dataIndex: 'dustDeduction', render: (v) => v?.toFixed(4) },
                  { title: 'Net Wt. (gm)', dataIndex: 'netWeight', render: (v) => v?.toFixed(4) },
                  { title: 'Est. Rate/gm', dataIndex: 'estimatedRatePerGram', render: (v) => formatCurrency(v) },
                  { title: 'Total Price', dataIndex: 'totalPrice', render: (v) => <strong>{formatCurrency4(v)}</strong> },
                ]}
                summary={() => (
                  <>
                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={3} align="right"><strong>Totals</strong></Table.Summary.Cell>
                      <Table.Summary.Cell align="center"><strong>{c.totalGrossWeight?.toFixed(4)} gm</strong></Table.Summary.Cell>
                      <Table.Summary.Cell align="center"><strong>{c.totalStoneDeduction?.toFixed(4)} gm</strong></Table.Summary.Cell>
                      <Table.Summary.Cell align="center"><strong>{c.totalDustDeduction?.toFixed(4)} gm</strong></Table.Summary.Cell>
                      <Table.Summary.Cell align="center"><strong>{c.totalNetWeight?.toFixed(4)} gm</strong></Table.Summary.Cell>
                      <Table.Summary.Cell />
                      <Table.Summary.Cell align="right"><strong>{formatCurrency4(c.estimatedTotalAmount)}</strong></Table.Summary.Cell>
                    </Table.Summary.Row>
                    {(c.processingFee > 0 || c.processingFeeAmount > 0) && (
                      <Table.Summary.Row style={{ background: '#fafafa' }}>
                        <Table.Summary.Cell colSpan={8} align="right">
                          <Text type="secondary">Processing Fee ({(c.processingFee || 0)}%)</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell align="right" style={{ paddingRight: 16 }}>
                          <Text type="secondary" strong>- {formatCurrency4(c.processingFeeAmount)}</Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                    {roundingUpAmt !== 0 && (
                      <Table.Summary.Row style={{ background: '#fafafa' }}>
                        <Table.Summary.Cell colSpan={8} align="right">
                          <Text type="secondary">Rounding Up</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell align="right" style={{ paddingRight: 16 }}>
                          <Text type="secondary" strong style={{ color: roundingUpAmt > 0 ? '#52c41a' : '#cf1322' }}>
                            {roundingUpAmt > 0 ? '+ ' : '- '}{formatCurrency(Math.abs(roundingUpAmt))}
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                    {(estRoundOff) !== 0 && (
                      <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={8} align="right">
                          <Text type="secondary">Round Off (to nearest ₹)</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell align="right" style={{ paddingRight: 16 }}>
                          <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>{formatRoundOff(estRoundOff)}</Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                    <Table.Summary.Row style={{ borderTop: '3px solid #1890ff', background: '#f0f5ff' }}>
                      <Table.Summary.Cell colSpan={8} align="right">
                        <Text strong style={{ fontSize: 16 }}>Final Amount (to be used)</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right" style={{ paddingRight: 16 }}>
                        <Text strong style={{ fontSize: 16, color: '#3f8600' }}>
                          {formatCurrency(approxFinal)}
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </>
                )}
              />
            </>
          )}
        </div>
      ),
    },

    {
      key: 'documents',
      label: styledTabLabel('documents', <FileTextOutlined />, ' Loan Documents'),
      children: (
        <div ref={registerRef('documents')}>
          {renderNextStepInfo("Upload all mandatory documents and get them approved to unlock the NEXT STEP.")}
          <DocumentUploadSection
            caseId={c._id}
            caseData={c}
            documents={c.documents || []}
            onDocumentsChanged={handleDocumentsChanged}
            disabled={isCaseLocked}
            onNextTab={goToNextTab}
          />
        </div>
      ),
    },

    /* ── 3. Pre-Loan Agreement ── */
    {
      key: 'pre-loan-agreement',
      label: styledTabLabel('pre-loan-agreement', <AuditOutlined />, ' Pre-Loan Agreement'),
      children: (
        <div ref={registerRef('pre-loan-agreement')}>
          {renderNextStepInfo("Generate the Pre-Loan Agreement to unlock the NEXT STEP.")}
          <GoldAgreementSection
            caseId={c._id}
            caseData={caseData}
            goldReceipt={goldReceipt}
            onAgreementCreated={handleAgreementCreated}
            filterType="PRE_LOAN_TAKEOVER"
          />
        </div>
      ),
    },

    /* ── 4. Advance Payments & Vouchers ── */
    {
      key: 'advances',
      label: styledTabLabel('advances', <DollarOutlined />, ' Advance Payments & Vouchers'),
      children: (
        <div ref={registerRef('advances')}>
          {renderNextStepInfo('Click "Add Payment with Voucher" → Select Advance Category as "Loan Payment To Lender" → Generate Preview → Submit for Approval, and obtain approval to unlock the NEXT STEP.')}
          <AdvancePaymentLedgerTable
            caseId={id}
            caseData={caseData}
            onPaymentAdded={handlePaymentAdded}
          />
          <Divider />
          <VoucherTable
            caseId={id}
            refreshTrigger={voucherRefreshTrigger}
            onVoucherUpdate={() => loadCase()}
          />
        </div>
      ),
    },

    /* ── 5. Gold Receipt & Valuation ── */
    {
      key: 'gold-receipt',
      label: styledTabLabel('gold-receipt', <GoldOutlined />, ' Gold Receipt'),
      children: (
        <div ref={registerRef('gold-receipt')}>
          {renderNextStepInfo("Create a Gold Receipt and complete the valuation to unlock the NEXT STEP.")}
          <Spin spinning={receiptLoading}>
            {!goldReceipt ? (
              <div className="mb-3">
                {c.status === 'FULLY_PAID_AWAITING_RELEASE' || c.status === 'GOLD_RECEIVED' ? (
                  <Space direction="vertical" size="middle">
                    <Text type="secondary">No gold receipt yet. Create one to lock the final rate and proceed to settlement.</Text>
                    <Button
                      type="primary"
                      icon={<GoldOutlined />}
                      onClick={() => navigate(`/pledge-release-cases/gold-receipt/create?caseId=${c._id}`)}
                    >
                      Create Gold Receipt
                    </Button>
                  </Space>
                ) : (
                  <Text type="secondary">
                    {['LEAD_IDENTIFIED', 'DOCS_VERIFIED', 'ADVANCE_APPROVED', 'PART_PAYMENT_DONE'].includes(c.status)
                      ? 'Gold receipt can be created after all loan payments are complete (FULLY PAID AWAITING RELEASE).'
                      : 'No gold receipt found for this case.'}
                  </Text>
                )}
              </div>
            ) : (
              <>
                {/* Receipt Summary Cards */}
                <Row gutter={[16, 16]} className="mb-3">
                  <Col xs={24} sm={8} md={6}>
                    <Card size="small" bordered style={{ background: '#fffbe6' }}>
                      <Statistic title="Receipt ID" value={goldReceipt.receiptId} valueStyle={{ fontSize: 16 }} prefix={<FileTextOutlined />} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={8} md={6}>
                    <Card size="small" bordered>
                      <Statistic title="Gross Gold Value" value={goldReceipt.finalGoldValue} precision={4} prefix={<GoldOutlined />} suffix="₹" valueStyle={{ fontSize: 16 }} />
                    </Card>
                  </Col>
                  {(goldReceipt.processingFeePercent > 0 || goldReceipt.processingFeeAmount > 0) && (
                    <Col xs={24} sm={8} md={6}>
                      <Card size="small" bordered style={{ background: '#fff2f0' }}>
                        <Statistic
                          title={`Processing Fee (${goldReceipt.processingFeePercent || 0}%)`}
                          value={goldReceipt.processingFeeAmount || 0}
                          precision={4} prefix="- ₹"
                          valueStyle={{ fontSize: 16, color: '#cf1322' }}
                        />
                      </Card>
                    </Col>
                  )}
                  <Col xs={24} sm={8} md={6}>
                    <Card size="small" bordered style={{ background: '#f6ffed' }}>
                      <Statistic title="Final Net Value" value={goldReceipt.finalNetValue} precision={0} prefix="₹" valueStyle={{ fontSize: 16, color: '#3f8600' }} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={8} md={6}>
                    <Card size="small" bordered>
                      <Statistic title="Gross Weight" value={goldReceipt.finalTotalGrossWeight || 0} precision={4} suffix="gm" valueStyle={{ fontSize: 16 }} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={8} md={6}>
                    <Card size="small" bordered>
                      <Statistic title="Final Net Weight" value={goldReceipt.finalTotalNetWeight} precision={4} suffix="gm" valueStyle={{ fontSize: 16 }} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={8} md={6}>
                    <Card size="small" bordered>
                      <Statistic title="Valuation Date" value={goldReceipt.valuationDate ? dayjs(goldReceipt.valuationDate).format('DD MMM YYYY') : '-'} valueStyle={{ fontSize: 14 }} />
                    </Card>
                  </Col>
                </Row>

                {/* Final Gold Items Table - Post Valuation */}
                {goldReceipt.finalGoldItems && goldReceipt.finalGoldItems.length > 0 && (
                  <>
                    <Divider orientation="left">Final Gold Items — Post Valuation</Divider>
                    <Table
                      dataSource={goldReceipt.finalGoldItems.map((item, idx) => ({ ...item, key: idx }))}
                      pagination={false}
                      size="small"
                      bordered
                      scroll={{ x: 'max-content' }}
                      columns={[
                        { title: '#', render: (_, __, idx) => idx + 1, width: 50 },
                        { title: 'Item Name', dataIndex: 'itemName' },
                        { title: 'Purity (%)', dataIndex: 'purity', render: (v) => `${v}%` },
                        { title: 'Gross Wt. (gm)', dataIndex: 'grossWeight', render: (v) => v?.toFixed(4) },
                        { title: 'Stone/Wax Ded. (gm)', dataIndex: 'stoneWaxDeduction', render: (v) => v?.toFixed(4) },
                        { title: 'Dust Ded. (gm)', dataIndex: 'dustDeduction', render: (v) => v?.toFixed(4) },
                        { title: 'Net Wt. (gm)', dataIndex: 'netWeight', render: (v) => v?.toFixed(4) },
                        { title: 'Final Rate/gm', dataIndex: 'finalRatePerGram', render: (v) => formatCurrency(v) },
                        { title: 'Total Price', dataIndex: 'totalPrice', render: (v) => <strong>{formatCurrency4(v)}</strong> },
                      ]}
                      summary={() => (
                        <>
                          <Table.Summary.Row>
                            <Table.Summary.Cell colSpan={3} align="right"><strong>Totals</strong></Table.Summary.Cell>
                            <Table.Summary.Cell align="center"><strong>{goldReceipt.finalTotalGrossWeight?.toFixed(4)} gm</strong></Table.Summary.Cell>
                            <Table.Summary.Cell align="center"><strong>{goldReceipt.finalGoldItems.reduce((s, item) => s + (item.stoneWaxDeduction || 0), 0).toFixed(4)} gm</strong></Table.Summary.Cell>
                            <Table.Summary.Cell align="center"><strong>{goldReceipt.finalGoldItems.reduce((s, item) => s + (item.dustDeduction || 0), 0).toFixed(4)} gm</strong></Table.Summary.Cell>
                            <Table.Summary.Cell align="center"><strong>{goldReceipt.finalTotalNetWeight?.toFixed(4)} gm</strong></Table.Summary.Cell>
                            <Table.Summary.Cell />
                            <Table.Summary.Cell align="right"><strong>{formatCurrency4(goldReceipt.finalGoldValue)}</strong></Table.Summary.Cell>
                          </Table.Summary.Row>
                          {(goldReceipt.processingFeePercent > 0 || goldReceipt.processingFeeAmount > 0) && (
                            <Table.Summary.Row style={{ background: '#fafafa' }}>
                              <Table.Summary.Cell colSpan={8} align="right">
                                <Text type="secondary">Processing Fee ({goldReceipt.processingFeePercent || 0}%)</Text>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell align="right" style={{ paddingRight: 16 }}>
                                <Text type="secondary" strong>- {formatCurrency4(goldReceipt.processingFeeAmount)}</Text>
                              </Table.Summary.Cell>
                            </Table.Summary.Row>
                          )}
                          {(goldReceipt.roundingUpAmount || 0) !== 0 && (
                            <Table.Summary.Row style={{ background: '#fafafa' }}>
                              <Table.Summary.Cell colSpan={8} align="right">
                                <Text type="secondary">Rounding Up</Text>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell align="right" style={{ paddingRight: 16 }}>
                                <Text type="secondary" strong style={{ color: (goldReceipt.roundingUpAmount || 0) > 0 ? '#52c41a' : '#cf1322' }}>
                                  {(goldReceipt.roundingUpAmount || 0) > 0 ? '+ ' : '- '}{formatCurrency(Math.abs(goldReceipt.roundingUpAmount || 0))}
                                </Text>
                              </Table.Summary.Cell>
                            </Table.Summary.Row>
                          )}
                          {(calculateCaseRoundOff(goldReceipt.finalGoldValue, goldReceipt.processingFeeAmount, goldReceipt.roundingUpAmount, goldReceipt.finalNetValue)) !== 0 && (
                            <Table.Summary.Row>
                              <Table.Summary.Cell colSpan={8} align="right">
                                <Text type="secondary">Round Off (to nearest ₹)</Text>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell align="right" style={{ paddingRight: 16 }}>
                                <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>{formatRoundOff(calculateCaseRoundOff(goldReceipt.finalGoldValue, goldReceipt.processingFeeAmount, goldReceipt.roundingUpAmount, goldReceipt.finalNetValue))}</Text>
                              </Table.Summary.Cell>
                            </Table.Summary.Row>
                          )}
                          <Table.Summary.Row style={{ borderTop: '3px solid #1890ff', background: '#f0f5ff' }}>
                            <Table.Summary.Cell colSpan={8} align="right">
                              <Text strong style={{ fontSize: 16 }}>Final Net Value (to be used)</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell align="right" style={{ paddingRight: 16 }}>
                              <Text strong style={{ fontSize: 16, color: '#3f8600' }}>
                                {formatCurrency(goldReceipt.finalNetValue)}
                              </Text>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        </>
                      )}
                    />
                  </>
                )}

                <Space>
                  <Button type="link" icon={<GoldOutlined />} onClick={() => navigate(`/pledge-release-cases/gold-receipt/view?caseId=${c._id}`)}>
                    View Full Receipt Details
                  </Button>
                  {goldReceiptUnlocked && (
                    <Button
                      type="primary"
                      icon={<UnlockOutlined />}
                      onClick={() => navigate(`/pledge-release-cases/gold-receipt/edit?caseId=${c._id}`)}
                    >
                      Correct This Valuation
                    </Button>
                  )}
                </Space>
              </>
            )}
          </Spin>
        </div>
      ),
    },

    /* ── 6. Final Agreement ── */
    {
      key: 'final-agreement',
      label: styledTabLabel('final-agreement', <SafetyOutlined />, ' Final Agreement'),
      children: (
        <div ref={registerRef('final-agreement')}>
          {renderNextStepInfo("Generate the Final Purchase Agreement to unlock the NEXT STEP.")}
          <GoldAgreementSection
            caseId={c._id}
            caseData={caseData}
            goldReceipt={goldReceipt}
            onAgreementCreated={handleAgreementCreated}
            filterType="FINAL_PURCHASE"
          />
        </div>
      ),
    },

    /* ── 7. Settlement — the customer is paid BEFORE the bill is raised ── */
    {
      key: 'settlement',
      label: styledTabLabel('settlement', <CheckCircleOutlined />, ' Settlement'),
      children: (
        <div ref={registerRef('settlement')}>
          {renderNextStepInfo("Pay the customer the full settlement amount here. Once it is cleared, the Bill step unlocks.")}
          <Spin spinning={finalizing} tip="Preparing settlement…">
            {isSettlementReady ? (
              <>
                {/* Settlement Breakdown */}
                <Divider orientation="left" style={{ marginTop: 0 }}>Settlement Breakdown</Divider>
                <Row gutter={[16, 16]} className="mb-3">
                  {canViewFinalGoldValue && (
                    <Col xs={24} sm={12} md={8}>
                      <Card size="small" bordered style={{ background: '#f0f5ff' }}>
                        <Statistic title="Final Net Value (after Processing Fee)" value={c.finalNetValue} precision={2} prefix="₹" valueStyle={{ color: '#1d39c4' }} />
                      </Card>
                    </Col>
                  )}
                  <Col xs={24} sm={12} md={8}>
                    <Card size="small" bordered>
                      <Statistic title="Total Loan Paid" value={c.totalLoanPaid} precision={2} prefix="₹" />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Card size="small" bordered>
                      <Statistic title="Customer Advance Paid" value={c.totalCustomerAdvancePaid} precision={2} prefix="₹" />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Card size="small" bordered style={{ background: c.finalSettlementAmount >= 0 ? '#f6ffed' : '#fff2f0' }}>
                      <Statistic
                        title="Net Settlement Amount (to pay customer)"
                        value={c.finalSettlementAmount}
                        precision={2} prefix="₹"
                        valueStyle={{ color: c.finalSettlementAmount >= 0 ? '#3f8600' : '#cf1322', fontWeight: 600 }}
                      />
                    </Card>
                  </Col>
                  {canViewActualProfit && (
                    <Col xs={24} sm={12} md={8}>
                      <Card size="small" bordered style={{ background: c.actualProfit >= 0 ? '#f6ffed' : '#fff2f0' }}>
                        <Statistic title="Actual Profit" value={c.actualProfit} precision={2} prefix="₹" valueStyle={{ color: c.actualProfit >= 0 ? '#3f8600' : '#cf1322' }} />
                      </Card>
                    </Col>
                  )}
                  {canViewActualProfit && (
                    <Col xs={24} sm={12} md={8}>
                      <Card size="small" bordered style={{ background: c.profitPercentage >= 0 ? '#f6ffed' : '#fff2f0' }}>
                        <Statistic title="Profit %" value={c.profitPercentage} precision={2} suffix="%" valueStyle={{ color: c.profitPercentage >= 0 ? '#3f8600' : '#cf1322' }} />
                      </Card>
                    </Col>
                  )}
                </Row>

                {/* Settlement Payments Section */}
                <Divider orientation="left">Settlement Payments</Divider>
                <SettlementPaymentTable
                  caseId={id}
                  caseData={caseData}
                  onPaymentAdded={handlePaymentAdded}
                />

                {/* Settlement Payment Vouchers Section (SEPARATE from Advance Vouchers) */}
                <Divider orientation="left">Settlement Payment Vouchers</Divider>
                <SettlementPaymentVoucherTable
                  caseId={id}
                  caseData={caseData}
                  refreshTrigger={voucherRefreshTrigger}
                  onVoucherUpdate={loadCase}
                />

                {c.closedAt && (
                  <Text type="secondary" style={{ fontSize: 12, marginTop: 12, display: 'block' }}>
                    Settled on {dayjs(c.closedAt).format('DD MMM YYYY, hh:mm A')}
                  </Text>
                )}
              </>
            ) : (
              <Text type="secondary">
                Settlement opens once the gold receipt and valuation are complete.
              </Text>
            )}
          </Spin>
        </div>
      ),
    },

    /* ── 8. Bill — the LAST step, raised against a completed settlement ── */
    {
      key: 'bill',
      label: styledTabLabel('bill', <FileDoneOutlined />, ' Bill'),
      children: (
        <div ref={registerRef('bill')}>
          {renderNextStepInfo("This is the final step. Generate the Bill — it carries every settlement payment and reference number, and closes the case.")}
          {c.billId ? (
            <Row align="middle" gutter={16} className="mb-3">
              <Col>
                <Button
                  type="primary"
                  ghost
                  icon={<FileTextOutlined />}
                  onClick={() => navigate(`/billing/viewCreatedBill/${typeof c.billId === 'object' ? c.billId._id : c.billId}`)}
                >
                  View Bill {c.settlementSnapshot?.bill?.billId ? `(${c.settlementSnapshot.bill.billId})` : ''}
                </Button>
              </Col>
              <Col>
                <Tag
                  color={c.status === 'BILL_GENERATED' || c.status === 'CLOSED' ? 'green' : 'blue'}
                  style={{ fontSize: 13, padding: '4px 14px' }}
                >
                  {(c.status || '').replace(/_/g, ' ')}
                </Tag>
              </Col>
            </Row>
          ) : canGenerateBill ? (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Alert
                type="success"
                showIcon
                message="Customer paid in full — ready to bill."
                description={`${formatCurrency(c.totalSettlementPaid)} paid to the customer. The bill will carry each payment, its mode and its reference number.`}
              />
              <div>
                <Button
                  type="primary"
                  size="large"
                  icon={<DollarOutlined />}
                  loading={settling}
                  onClick={handleGenerateBill}
                >
                  Generate Final Bill &amp; Close
                </Button>
              </div>
            </Space>
          ) : (
            <Alert
              type="info"
              showIcon
              message="The bill is the final step."
              description={
                isSettlementReady
                  ? `Pay the customer in full first — ${formatCurrency(Math.max(0, pledgeRemaining))} still remaining on the Settlement step.`
                  : 'Complete the gold receipt and valuation, then pay the customer on the Settlement step.'
              }
            />
          )}
        </div>
      ),
    },
  ];

  /* ════════════════════════════════════════
     RENDER
     ════════════════════════════════════════ */
  return (
    <>
      {contextHolder}
      {modalContextHolder}
      {deleteModalHolder}
      <div className="container-fluid" style={{ padding: 0 }}>
        {c?.isDeleted && (
          <div style={{ padding: '16px 16px 0' }}>
            <Alert
              type="error"
              showIcon
              message="This case has been deleted"
              description={`Deleted${c.deletedBy?.name ? ` by ${c.deletedBy.name}` : ''}${c.deletedAt ? ` on ${dayjs(c.deletedAt).format('DD MMM YYYY, hh:mm A')}` : ''}. It is hidden from all lists.`}
              action={isAdmin && (
                <Space direction="vertical">
                  <Button size="small" onClick={() => confirmRestore({ what: `case ${c.caseId}`, onRestore: handleRestore })}>Restore</Button>
                  <Button size="small" danger onClick={() => confirmPermanentDelete({ what: `case ${c.caseId}`, onDelete: () => handleDelete(true) })}>
                    Delete permanently
                  </Button>
                </Space>
              )}
            />
          </div>
        )}
        {c?.autoComplete?.status === 'FAILED' && (canGenerateBill || canMoveToVault) && (
          <div style={{ padding: '16px 16px 0' }}>
            <Alert
              type="warning"
              showIcon
              message={`Automatic ${c.autoComplete.step === 'VAULT' ? 'move to vault' : 'bill generation'} failed — please complete it manually`}
              description={`${c.autoComplete.error || 'Unknown error'}. Use ${canGenerateBill ? 'Generate Final Bill & Close on the Bill tab' : 'the Move to Vault button above'}.`}
            />
          </div>
        )}
        {/* ── Sticky Progress Stepper ── */}
        <CaseProgressStepper
          caseData={caseData}
          goldReceipt={goldReceipt}
          agreements={agreements}
          activeTab={activeTab}
          onStepClick={handleStepClick}
        />

        {/* ── Header Card ── */}
        <div style={{ padding: '16px 16px 0' }}>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Space size="middle" align="center">
                  <Title level={3} style={{ margin: 0 }}>{c.caseId}</Title>
                  <Tag color={STATUS_COLORS[c.status] || 'default'} style={{ fontSize: 12 }}>
                    {(c.status || '').replace(/_/g, ' ')}
                  </Tag>
                </Space>
              </Col>
              <Col>
                <Space wrap>
                  {canMoveToVault && (
                    <Popconfirm
                      title="Move gold to branch vault?"
                      description="This will record the gold in the branch vault and generate a QR label."
                      onConfirm={handleMoveToVault}
                      okText="Move to Vault"
                      cancelText="Cancel"
                    >
                      <Button
                        size="small"
                        type="primary"
                        icon={<SafetyCertificateOutlined />}
                        loading={vaultLoading}
                        style={{ background: '#52c41a', borderColor: '#52c41a' }}
                      >
                        Move to Vault
                      </Button>
                    </Popconfirm>
                  )}
                  {c.inventoryStatus === 'IN_BRANCH_VAULT' && (
                    <Button
                      size="small"
                      type="default"
                      icon={<SafetyCertificateOutlined />}
                      onClick={() => setVaultLabelVisible(true)}
                    >
                      Print Vault Label
                    </Button>
                  )}
                  <Button size="small" onClick={() => navigate('/pledge-release-cases')}>Back</Button>
                  <ReconcileButton
                    caseId={c._id}
                    reconcileState={reconcileState}
                    canReconcile={canReconcile}
                    onChanged={refreshAfterReconcile}
                  />
                  {canEditModule('pledgeReleaseCase') && (
                    <Button
                      size="small"
                      type={caseDetailsUnlocked ? 'primary' : 'default'}
                      onClick={() => navigate(`/pledge-release-cases/edit/${c._id}`)}
                      // A settled case is closed to editing — unless an approved
                      // reconciliation has opened it for exactly one correction.
                      disabled={isCaseLocked && !caseDetailsUnlocked}
                      title={
                        caseDetailsUnlocked
                          ? 'Unlocked by an approved reconciliation — one corrective save'
                          : isCaseLocked
                            ? 'Case is settled — raise a reconciliation to correct it'
                            : undefined
                      }
                    >
                      {caseDetailsUnlocked
                        ? <UnlockOutlined style={{ marginRight: 4 }} />
                        : isCaseLocked && <LockOutlined style={{ marginRight: 4 }} />}
                      Edit
                    </Button>
                  )}
                  {can('pledgeReleaseCase', 'delete') && !c.isDeleted && (
                    <Button danger size="small" disabled={isCaseLocked && !isAdmin}
                      title={isCaseLocked && !isAdmin ? 'Case is settled — deletion is disabled' : undefined}
                      onClick={() => (isCaseLocked
                        // Settled: money has moved, so only an admin's permanent delete applies.
                        ? confirmPermanentDelete({ what: `case ${c.caseId}`, onDelete: () => handleDelete(true) })
                        : confirmDelete({ what: `case ${c.caseId}`, onDelete: handleDelete }))}
                    >Delete</Button>
                  )}
                </Space>
              </Col>
            </Row>
          </Card>
        </div>

        {/* ── Reconciliation state — pending approval, or unlocked to correct ── */}
        <div style={{ padding: '0 16px' }}>
          <ReconcileBanner
            caseId={c._id}
            reconcileState={reconcileState}
            canReconcile={canReconcile}
            onChanged={refreshAfterReconcile}
            onEditTarget={handleEditReconcileTarget}
          />
          <ReconcileHistory reconcileState={reconcileState} />
        </div>

        {/* ── Tabbed Content ── */}
        <div style={{ padding: '0 16px 16px' }}>
          <Card bodyStyle={{ padding: '12px 16px' }}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              type="card"
              size="small"
              items={tabItems}
              tabBarExtraContent={nextTabButton}
              destroyInactiveTabPane={false}
            />
          </Card>
        </div>
      </div>

      {/* ── Gold Vault Label ── */}
      <GoldVaultLabel
        caseData={caseData}
        caseModel="PledgeReleaseCase"
        goldReceipt={goldReceipt}
        visible={vaultLabelVisible}
        onClose={() => setVaultLabelVisible(false)}
      />
    </>
  );
};

export default PledgeReleaseCaseDetails;
